// src/components/sections/cases.js
import React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import { IconBookmark } from '@components/icons';

const StyledCasesSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 600px) {
    margin: 0 10px 5px 10px;
  }

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
    margin-bottom: 0.5rem;
  }

  .cases-short {
    color: var(--navy);
    font-size: 20px;
    margin: 0 0 15px;
  }

  .cases-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    flex: 1;
    grid-template-columns: repeat(3, minmax(200px, 1.5fr));
    grid-auto-rows: 1fr;
    grid-gap: 15px;
    position: relative;
    margin-top: 50px;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    }
  }
`;

const StyledCase = styled.li`
  transition: var(--transition);
  cursor: default;

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .case-inner {
        transform: translateY(-7px);
      }
    }
  }

  .case-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 2rem 1.75rem;
    border-radius: var(--border-radius);
    background-color: var(--navy);
    border: 1px solid #171717;
    transition: var(--transition);
    overflow: auto;
    height: 100%;
  }

    .case-icon {
      ${({ theme }) => theme.mixins.flexBetween};
      color: var(--green);
      margin-bottom: 30px;
      margin-left: -5px;

      svg {
        width: 40px;
        height: 40px;
      }
    }

    .case-title {
      margin: 0 0 10px;
      color: var(--lightest-slate);
      font-size: var(--fz-xxl);

      a {
        position: static;

        &:before {
          content: none;
        }
      }
    }

    .case-desc {
      color: var(--light-slate);
      font-size: 17px;
    }

    .case-date {
      color: var(--light-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      text-transform: uppercase;
      margin: 10px 0 0 0;
    }
  }
`;

const Cases = () => {
  const data = useStaticQuery(graphql`
    query HomeCases {
      allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/posts/" }, frontmatter: { draft: { ne: true } } }
        sort: { fields: [frontmatter___date], order: DESC }
        limit: 6
      ) {
        edges {
          node {
            frontmatter {
              title
              description
              slug
              date
            }
          }
        }
      }
    }
  `);

  const posts = data?.allMarkdownRemark?.edges ?? [];
  if (!posts.length) return null;

  return (
    <StyledCasesSection id="cases">
      <h2>Cases</h2>

      <ul className="cases-grid">
        {posts.map(({ node }, i) => {
          const { title, description, slug, date } = node.frontmatter;
          const formattedDate = new Date(date).toLocaleDateString('nl-NL');

          return (
            <StyledCase key={`${slug}-${i}`}>
              <div className="case-inner">
                <div className="case-icon">
                  <IconBookmark />
                </div>
                <h5 className="case-title">
                  <Link to={slug}>{title}</Link>
                </h5>
                <p className="case-desc">{description}</p>
                <span className="case-date">{formattedDate}</span>
              </div>
            </StyledCase>
          );
        })}
      </ul>
    </StyledCasesSection>
  );
};

export default Cases;
